<script setup lang="ts">
import InternalLinks from "@/components/seo/InternalLinks.vue";
import { seoConfig } from "@/config/seo.config";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

useHead({
  title: seoConfig.roza.title,
  meta: [
    {
      name: "description",
      content: seoConfig.roza.description,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: seoConfig.roza.canonical,
    },
  ],
});

const { messages, locale } = useI18n();

const rozaPage = computed(() => {
  return messages.value[locale.value]?.pages?.roza;
});

type RozaCategoryKey =
  | "basics"
  | "breaks"
  | "qazaKaffarah"
  | "common"
  | "womenSpecial";

const activeCategory = ref<RozaCategoryKey>("basics");
const openQuestion = ref<string | null>(null);

const categories = computed(() => rozaPage.value?.categories ?? []);

const categoryCounts = computed(() => {
  const counts: Partial<Record<RozaCategoryKey, number>> = {};
  const questions = rozaPage.value?.questions ?? [];

  for (const item of questions) {
    counts[item.category as RozaCategoryKey] =
      (counts[item.category as RozaCategoryKey] ?? 0) + 1;
  }

  return counts;
});

const filteredQuestions = computed(() => {
  const questions = rozaPage.value?.questions ?? [];
  return questions.filter(
    (item: { category: RozaCategoryKey }) => item.category === activeCategory.value,
  );
});

const toggle = (question: string) => {
  openQuestion.value = openQuestion.value === question ? null : question;
};

watch(
  categories,
  (list) => {
    if (!list.length) return;
    const exists = list.some(
      (item: { key: RozaCategoryKey }) => item.key === activeCategory.value,
    );
    if (!exists) {
      activeCategory.value = list[0].key;
    }
  },
  { immediate: true },
);

const { generateFAQSchema, generateWebPageSchema, addSchema } = useSchemaMarkup();

const faqSchema = computed(() => {
  if (!rozaPage.value?.questions) return null;

  return generateFAQSchema(
    rozaPage.value.questions.map((item: { q: string; a: string }) => ({
      question: item.q,
      answer: item.a,
    })),
  );
});

const webPageSchema = computed(() =>
  generateWebPageSchema({
    name: seoConfig.roza.title,
    description: seoConfig.roza.description,
    url: seoConfig.roza.canonical,
  }),
);

onMounted(() => {
  addSchema(webPageSchema.value);
  if (faqSchema.value) {
    addSchema(faqSchema.value);
  }
});
</script>

<template>
  <section
    v-if="rozaPage"
    class="w-full max-w-full sm:max-w-2xl mx-auto px-4 py-6 space-y-6"
  >
    <header class="space-y-2">
      <h1 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
        {{ rozaPage.title }}
      </h1>
      <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
        {{ rozaPage.intro }}
      </p>
    </header>

    <NuxtLink
      to="/ramadan/rehnumai/"
      class="block rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/40 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
    >
      <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Ramadan Rehnumai
      </p>
      <p class="mt-1 text-sm text-gray-700 dark:text-gray-200">
        Sehri o Iftar ke liye Sehat aur Sunnah guide dekhein.
      </p>
    </NuxtLink>

    <div class="flex flex-wrap gap-2">
      <button
        v-for="chip in categories"
        :key="chip.key"
        class="px-4 py-1.5 rounded-full text-sm whitespace-nowrap"
        :class="
          activeCategory === chip.key
            ? 'bg-gray-800 text-white dark:bg-emerald-500 dark:text-white'
            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
        "
        @click="activeCategory = chip.key"
      >
        {{ chip.label }}
        <span
          class="text-xs ml-1"
          :class="
            activeCategory === chip.key
              ? 'text-gray-200 dark:text-emerald-100'
              : 'text-gray-500 dark:text-gray-400'
          "
        >
          ({{ categoryCounts[chip.key] ?? 0 }})
        </span>
      </button>
    </div>

    <div
      class="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700"
    >
      <div
        v-for="item in filteredQuestions"
        :key="item.q"
        class="px-4 py-3"
      >
        <button
          type="button"
          class="w-full flex justify-between items-start text-left gap-3"
          @click="toggle(item.q)"
        >
          <span class="text-base font-medium text-gray-900 dark:text-gray-100">
            {{ item.q }}
          </span>
          <span class="text-gray-400 dark:text-gray-300 text-lg">
            {{ openQuestion === item.q ? "-" : "+" }}
          </span>
        </button>

        <div
          v-if="openQuestion === item.q"
          class="mt-3 text-base text-gray-700 dark:text-gray-300 leading-relaxed"
        >
          {{ item.a }}
        </div>
      </div>
    </div>

    <InternalLinks current="roza" />
  </section>
</template>
